import { ReactNode, useCallback, Key as ReactKey, type JSX } from "react";
import { ComboBox as AriaComboBox } from "react-aria-components";
import { useLabel } from "react-aria";

import { AnalyticsCommonProps } from "hooks/useAnalytics";
import useEnsureId from "hooks/useEnsureId";

import { getColors } from "../Form/Field/utils";
import TooltipInfo from "../TooltipInfo";
import ComboBoxEmptyCollection from "./EmptyCollection";
import ComboBoxInput, { ComboBoxInputProps } from "./Input";
import styles from "./styles.module.css";
import ListBoxPopover from "../ListBox/Popover";
import ListBox from "../ListBox";
import ListBoxScrollableWrapper from "../ListBox/ScrollableWrapper";
import FormFieldHelperText from "../Form/Field/HelperText";
import Box from "../Box";
import FormFieldAriaLabel from "../Form/Field/AriaLabel";
import { BaseCollection, SectionBaseCollection } from "./types";
import useSkipDuplicates from "./useSkipDuplicates";

type ComboBoxProps<
  Collection extends BaseCollection | SectionBaseCollection<BaseCollection, SectionProps>,
  Key extends string,
  SectionProps extends object = object,
> = {
  id?: string;
  /**
   * label is optional, but if it's not provided you need to connect the label via ariaLabelledBy attribute
   */
  label?: string;
  placeholder?: string;
  helperText?: string;
  error?: string;
  value?: string | null;
  items: Array<Collection>;
  onChange: (value: Key | null) => void;
  inputValue?: string;
  renderInput?: (input: ComboBoxInputProps) => ReactNode;
  onInputChange?: (value: string) => void;
  children: (Collection: Collection) => JSX.Element;
  tooltipInfo?: ReactNode;
  tooltipAnalyticsPage?: AnalyticsCommonProps["analyticsPage"];
  tooltipAnalyticsTitle?: AnalyticsCommonProps["analyticsTitle"];
  tooltipAnalyticsProps?: AnalyticsCommonProps["analyticsProps"];
  isDisabled?: boolean;
  isLoading?: boolean;
  size?: ComboBoxInputProps["size"];
  color?: ComboBoxInputProps["color"];
};

const ComboBox = <
  Collection extends BaseCollection | SectionBaseCollection<BaseCollection, SectionProps>,
  Key extends string,
  SectionProps extends object = object,
>({
  id: propsId,
  children,
  label,
  helperText,
  placeholder = "Type in or select from the list",
  error,
  value,
  size,
  color,
  renderInput = (props) => <ComboBoxInput {...props} />,
  onChange,
  inputValue,
  onInputChange,
  items,
  tooltipInfo,
  tooltipAnalyticsPage,
  tooltipAnalyticsTitle,
  tooltipAnalyticsProps,
  isDisabled,
  isLoading,
}: ComboBoxProps<Collection, Key, SectionProps>) => {
  const id = useEnsureId(propsId);
  const { primaryIconColor } = getColors(color);

  const handleSelectionChange = useCallback(
    (value: ReactKey | null) => {
      if (value === null || value === undefined) {
        return onChange(value);
      }

      return onChange(String(value) as Key);
    },
    [onChange]
  );

  const deduplicatedItems = useSkipDuplicates(items);

  const { labelProps, fieldProps } = useLabel({
    label,
    id,
  });

  return (
    <Box gap="small" direction="column" fullWidth>
      {label && (
        <Box gap="small" align="center" fullWidth>
          <FormFieldAriaLabel {...labelProps} label={label} color={color} />
          {tooltipInfo && (
            <TooltipInfo
              variant="modal"
              iconColor={primaryIconColor}
              analyticsPage={tooltipAnalyticsPage}
              analyticsTitle={tooltipAnalyticsTitle}
              analyticsProps={tooltipAnalyticsProps}
            >
              {tooltipInfo}
            </TooltipInfo>
          )}
        </Box>
      )}
      <AriaComboBox
        className={styles.comboBox}
        menuTrigger="focus"
        selectedKey={value}
        onSelectionChange={handleSelectionChange}
        inputValue={inputValue}
        onInputChange={onInputChange}
        isInvalid={!!error}
        isDisabled={isDisabled}
        allowsEmptyCollection
        {...fieldProps}
      >
        <Box gap="small" direction="column">
          {renderInput({
            id,
            placeholder,
            isError: !!error,
            isDisabled,
            isLoading,
            size,
            color,
          })}
          <FormFieldHelperText helperText={helperText} error={error} id={id} color={color} />
        </Box>

        <ListBoxPopover>
          <ListBoxScrollableWrapper>
            <ListBox<Collection>
              items={deduplicatedItems}
              renderEmptyState={() => <ComboBoxEmptyCollection isLoading={isLoading} />}
            >
              {children}
            </ListBox>
          </ListBoxScrollableWrapper>
        </ListBoxPopover>
      </AriaComboBox>
    </Box>
  );
};

ComboBox.displayName = "DS.ComboBox";

export default ComboBox;
